// Custom-POI-Symbol-Helpers
// =========================
//
// Wave 14cf-fix10: per-Kategorie-Icon, NICHT per-Quelle. Vorher hatten wir
// Admin (orange) und User (teal) als getrennte Layer mit unterschiedlichen
// Icons — User-Feedback: "wieso sieht ein selbst-markierter Brunnen anders
// aus als ein OSM-Brunnen?". Quelle ist UX-mässig die falsche Achse, die
// Kategorie ist was zählt.
//
// Architektur jetzt:
//   - poiSymbol.ts (drinking_water)  — sky-blue Droplet, gemischt
//                                       OSM + admin + user.
//   - customPoiSymbol.ts (HIER)      — alle anderen Kategorien
//                                       (cafe / pranzo / bike_shop / beach /
//                                       viewpoint), gemischt admin + user.
//                                       Per-Kategorie eigene Brand-Farbe.

import type maplibregl from 'maplibre-gl';
import { USER_POI_CATEGORIES } from '../userPois';

export const CUSTOM_POI_SOURCE = 'adt-custom-poi';
export const CUSTOM_POI_LAYER = 'adt-custom-poi-symbols';
export const CUSTOM_POI_ICON_PREFIX = 'adt-custom-poi-';

export const CUSTOM_POI_MIN_ZOOM = 11;

// Brand-Farben pro Kategorie. Bleiben in einer kleinen Map damit sie sich
// leicht erweitern lassen wenn neue Kategorien dazukommen. Falls eine ID
// nicht in der Map steht, fällt das Icon auf einen neutralen Stil zurück.
const CATEGORY_COLORS: Record<string, string> = {
  drinking_water: 'rgba(14,165,233,0.95)',  // sky-500 — synced mit poiSymbol
  cafe: 'rgba(120,72,33,0.95)',             // brown — Café-Optik
  pranzo: 'rgba(234,88,12,0.95)',           // orange-700 — Essen
  bike_shop: 'rgba(39,39,42,0.95)',         // zinc-800 — Werkzeug-Schwarz
  beach: 'rgba(234,179,8,0.95)',            // yellow-500 — Sand
  viewpoint: 'rgba(22,163,74,0.95)',        // green-600 — Aussicht
};
const FALLBACK_COLOR = 'rgba(99,102,241,0.95)'; // indigo-500 für unbekannte

function drawAndRegister(
  map: maplibregl.Map,
  iconName: string,
  emoji: string,
  bgColor: string,
): void {
  if (map.hasImage(iconName)) return;
  const SIZE = 52;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const radius = SIZE / 2 - 4;

  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = bgColor;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font =
    '28px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
  ctx.fillText(emoji, cx, cy + 1);

  const imageData = ctx.getImageData(0, 0, SIZE, SIZE);
  map.addImage(iconName, imageData as unknown as HTMLImageElement, {
    pixelRatio: 2,
  });
}

/**
 * Registriert ein Icon pro Kategorie. Wird beim styledata-Event neu
 * aufgerufen (Style-Wechsel leert die Image-Registry). Idempotent.
 */
export function ensureCustomPoiIcons(map: maplibregl.Map): void {
  for (const cat of USER_POI_CATEGORIES) {
    drawAndRegister(
      map,
      `${CUSTOM_POI_ICON_PREFIX}${cat.id}`,
      cat.emoji,
      CATEGORY_COLORS[cat.id] ?? FALLBACK_COLOR,
    );
  }
}

/** Source + Layer einrichten. Idempotent. */
export function ensureCustomPoiSourceAndLayer(map: maplibregl.Map): void {
  if (!map.getSource(CUSTOM_POI_SOURCE)) {
    map.addSource(CUSTOM_POI_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getLayer(CUSTOM_POI_LAYER)) {
    map.addLayer({
      id: CUSTOM_POI_LAYER,
      type: 'symbol',
      source: CUSTOM_POI_SOURCE,
      minzoom: CUSTOM_POI_MIN_ZOOM,
      layout: {
        'icon-image': ['concat', CUSTOM_POI_ICON_PREFIX, ['get', 'category']],
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
    });
  }
}

export function setCustomPoiData(
  map: maplibregl.Map,
  features: GeoJSON.Feature<GeoJSON.Point>[],
): void {
  const src = map.getSource(CUSTOM_POI_SOURCE) as
    | maplibregl.GeoJSONSource
    | undefined;
  if (!src) return;
  src.setData({ type: 'FeatureCollection', features });
}

export function removeCustomPoiSymbol(map: maplibregl.Map): void {
  try {
    if (map.getLayer(CUSTOM_POI_LAYER)) map.removeLayer(CUSTOM_POI_LAYER);
    if (map.getSource(CUSTOM_POI_SOURCE)) map.removeSource(CUSTOM_POI_SOURCE);
  } catch {
    /* Map zerstört */
  }
}
